import TermsCondition from "components/Agreements/TermsCondition";
import NavComponent from "components/Navigation/NavComponent";
import AuthContext from "context/AuthContext";
import StoreUserProfile from "Pages/dashboard/Profile/StoreUserProfile";
import ApiKeys from "Pages/dashboard/store/ApiKeys/ApiKeys";
import PlansSetup from "Pages/dashboard/PureBrand/Plans/PlansSetup";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Tabs } from "react-bootstrap";
import Gethelp from "./Gethelp";
import "./settings.css";
import UsefulLinks from "./UsefulLinks";
import Dots from "components/Dots/Dots";
import PrivacyPolShopify from "components/Agreements/PrivacyPolShopify";
import AdminDots from "components/AdminDots/AdminDots";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import PurebrandLoader from "components/Loader/PurebrandLoader";
import StoreTermsCondition from "components/Agreements/StoreTermsCondition";
import useCan from "hooks/useCan";
import { CAPABILITIES } from "utils/capabilities";
import { ROLES } from "utils/constants";

/**
 * Settings is one page shared by every portal, so each tab declares who may
 * see it. `page` is the `?page=` query value used for deep links.
 *
 * react-bootstrap mounts every pane, so a tab may only be listed when its
 * content is safe to load for that user (a 403 would log them out).
 */
const buildTabs = ({ user, can, isStoreUser }) => {
  const isHost = user?.role === ROLES.HOST.role;
  const isPurebrandAdmin = user?.role === ROLES.PUREBRAND_ADMIN.role;

  return [
    {
      key: "payout",
      page: "payout",
      visible: isHost,
      className: "features settings-payout-wrapper",
      title: (
        <>
          Configure Payment{" "}
          <Tooltip
            title={
              "You will only see the list of stores for connecting in whom you have at least one approved product"
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </>
      ),
      content: (
        <div className="dots-container" style={{ textAlign: "center" }}>
          <Dots />
        </div>
      ),
    },
    {
      key: "help",
      page: "help",
      visible: true,
      className: "features settings-help-wrapper",
      title: "Get Help",
      content: <Gethelp />,
    },
    {
      key: "planSettings",
      page: "planSettings",
      visible: isPurebrandAdmin,
      className: "features settings-plans-wrapper",
      title: "Set Plans",
      content: <PlansSetup />,
    },
    {
      key: "commissionSettings",
      page: "commissionSettings",
      visible: can(CAPABILITIES.COMMISSION_VIEW),
      className: "features settings-plans-wrapper",
      title: "Set Commission",
      content: <StoreUserProfile />,
    },
    {
      key: "adminDots",
      page: "adminDots",
      visible: can(CAPABILITIES.PAYMENT_SETUP_MANAGE),
      className: "features settings-plans-wrapper",
      title: "Payment Setup",
      content: <AdminDots />,
    },
    {
      key: "apiKeys",
      page: "apiKeys",
      visible: can(CAPABILITIES.APIKEYS_MANAGE),
      className: "features settings-plans-wrapper",
      title: "API Keys",
      content: <ApiKeys />,
    },
    {
      key: "usefulLinks",
      page: "usefulLinks",
      visible: isStoreUser,
      className: "features settings-plans-wrapper",
      title: "Helpful Links",
      content: <UsefulLinks />,
    },
    {
      key: "termsconditions",
      page: "term",
      visible: true,
      className: "features settings-tandc-wrapper",
      title: "Terms & Conditions",
      content: isHost ? <TermsCondition /> : <StoreTermsCondition />,
    },
    {
      key: "privacypolicy",
      page: "privacypolicy",
      visible: true,
      className: "features settings-privacy-wrapper",
      title: "Privacy Policy",
      content: <PrivacyPolShopify />,
    },
  ].filter((tab) => tab.visible);
};

const UserSettings = () => {
  const { user, isEmailVerified, emailVerificationLoading } = useContext(AuthContext);
  const { can, isStoreUser, isResolved } = useCan();
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = useMemo(
    () => buildTabs({ user, can, isStoreUser }),
    [user, can, isStoreUser]
  );
  const defaultKey = user?.role === ROLES.HOST.role ? "payout" : "help";
  const [activeKey, setActiveKey] = useState(defaultKey);

  // Deep links (`?page=`) only open tabs the user is allowed to see.
  useEffect(() => {
    const page = new URLSearchParams(location.search).get("page");
    const match = tabs.find((tab) => tab.page === page);
    setActiveKey(match ? match.key : defaultKey);
  }, [location.search, tabs, defaultKey]);

  const handleSelect = (key) => {
    setActiveKey(key);
    const page = tabs.find((tab) => tab.key === key)?.page ?? defaultKey;
    navigate(`${location.pathname}?page=${page}`);
  };

  if (emailVerificationLoading || !isResolved) {
    return <PurebrandLoader />;
  }

  if (!isEmailVerified && user?.role === ROLES.HOST.role) {
    return <></>;
  }

  return (
    <Tabs
      activeKey={activeKey}
      onSelect={handleSelect}
      defaultActiveKey={defaultKey}
      id="uncontrolled-tab-example"
      className="mb-4"
    >
      {tabs.map((tab) => (
        <NavComponent
          key={tab.key}
          eventKey={tab.key}
          title={tab.title}
          className={tab.className}
        >
          {tab.content}
        </NavComponent>
      ))}
    </Tabs>
  );
};

export default UserSettings;
